/**
 * The small pieces every layout of the Organization Overview shares: a holder
 * chip, the risk mix bar and the status badge. Nothing here fetches or decides
 * access -- each piece draws what the server already said.
 */

import { Briefcase, Package, UserCheck } from 'lucide-react';

import type { CurrentOwner } from '@/api/client';

import { riskClass, riskMix, type HolderKind, type OrgNode } from './overviewModel';

const KIND_ICON = { delivery: Package, sales: Briefcase, head: UserCheck } as const;

export const KIND_LABEL: Readonly<Record<HolderKind, string>> = {
  delivery: 'Delivery manager',
  sales: 'Sales manager',
  head: 'Project head',
};

/** The holder's name beside their kind's icon, or the muted placeholder. */
export function HolderChip({
  holder,
  kind,
  compact = false,
}: {
  holder: CurrentOwner | null;
  kind: HolderKind;
  compact?: boolean;
}) {
  const Icon = KIND_ICON[kind];
  return (
    <span className={`ov-holder ov-kind-${kind}${compact ? ' is-compact' : ''}`}>
      <span className="ov-ico" aria-hidden="true">
        <Icon />
      </span>
      {holder ? (
        <span className="ov-holder-name" title={`${KIND_LABEL[kind]}: ${holder.display_name}`}>
          {holder.display_name}
        </span>
      ) : (
        <span className="hint">Unassigned</span>
      )}
    </span>
  );
}

/** Holder with its role label underneath, for cards and the panel. */
export function HolderBox({ holder, kind }: { holder: CurrentOwner | null; kind: HolderKind }) {
  const Icon = KIND_ICON[kind];
  return (
    <div className={`ov-holderbox ov-kind-${kind}`}>
      <span className="ov-ico" aria-hidden="true">
        <Icon />
      </span>
      <span className="ov-holderbox-text">
        <span className="ov-holderbox-name">
          {holder ? holder.display_name : <span className="hint">Unassigned</span>}
        </span>
        <span className="ov-holderbox-role">{holder ? holder.delivery_role_name : KIND_LABEL[kind]}</span>
      </span>
    </div>
  );
}

/** High, medium and low engagements beneath a node, as one proportional bar. */
export function RiskBar({ node }: { node: OrgNode }) {
  const mix = riskMix(node.counts);
  const share = (count: number) => (mix.total === 0 ? 0 : (count / mix.total) * 100);
  return (
    <div
      className={`ov-risk${mix.total === 0 ? ' is-empty' : ''}`}
      role="img"
      aria-label={`Risk: ${mix.high} high, ${mix.medium} medium, ${mix.low} low`}
    >
      <i className="h" style={{ width: `${share(mix.high)}%` }} />
      <i className="m" style={{ width: `${share(mix.medium)}%` }} />
      <i className="l" style={{ width: `${share(mix.low)}%` }} />
    </div>
  );
}

/** An engagement's own risk label as a pill; an unrated one is a placeholder. */
export function RiskPill({ riskLevel }: { riskLevel: string | null }) {
  const level = riskClass(riskLevel);
  if (!level) return <span className="hint">Not rated</span>;
  return <span className={`ov-pill ov-pill-${level}`}>{riskLevel} risk</span>;
}

/** The LABEL is the server's; only the class is chosen here. */
export function StatusBadge({ node }: { node: OrgNode }) {
  if (node.isActive) return null;
  return <span className="badge pill-neutral">{node.statusName}</span>;
}

export function ContextTag({ node }: { node: OrgNode }) {
  if (node.inScope) return null;
  return (
    <span className="ov-context" title="Shown so the hierarchy reads in order. It is outside your scope.">
      Context
    </span>
  );
}
